import { memo, useMemo } from "react";
import { Box, Typography, TextField, FormControl, Tooltip } from "@mui/material";

const ConditionalValueInput = ({
  condition,
  value,
  onChange,
  selectedColumnType,
  fullWidth = true,
  size = "small",
  sx = {},
}) => {
  const getInputConfig = useMemo(() => {
    // Conditions that don't require value input
    const noValueConditions = ["is_empty", "is_not_empty", "today", "yesterday", "tomorrow"];
    if (noValueConditions.includes(condition)) {
      return { type: "none" };
    }

    const periodConditions = [
      "in_last_weeks",
      "in_last_months",
      "in_last_years",
      "in_next_weeks",
      "in_next_months",
      "in_next_years",
    ];
    if (periodConditions.includes(condition)) {
      return { type: "number", placeholder: "Enter number" };
    }

    const dateConditions = ["before", "after", "on_or_before", "on_or_after"];
    if (dateConditions.includes(condition)) {
      return { type: "date", placeholder: "" };
    }

    if (condition === "between") {
      return { type: "between" };
    }

    if (selectedColumnType === "number" || selectedColumnType === "percent") {
      return { type: "number", placeholder: "0" };
    }

    if (selectedColumnType === "text") {
      return { type: "text", placeholder: "Enter value" };
    }

    if (selectedColumnType === "select") {
      return { type: "text", placeholder: "Enter comma-separated values" };
    }

    return { type: "text", placeholder: "Enter value" };
  }, [condition, selectedColumnType]);

  if (getInputConfig.type === "none") {
    return <Box sx={{ flex: fullWidth ? 1 : undefined, ...sx }} />;
  }

  if (getInputConfig.type === "between") {
    const [value1 = "", value2 = ""] = (value || "").split("-");
    const tooltipTitle = value ? `${value1 || "From"} - ${value2 || "To"}` : "From - To";

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Box sx={{ flex: fullWidth ? 1 : undefined, display: "flex", gap: 0.5, alignItems: "center", ...sx }}>
          <TextField
            value={value1}
            onChange={(e) => {
              const newValue = `${e.target.value}-${value2}`;
              onChange(newValue);
            }}
            placeholder="From"
            size={size}
            type="number"
            fullWidth
          />
          <Typography sx={{ color: "text.secondary" }}>-</Typography>
          <TextField
            value={value2}
            onChange={(e) => {
              const newValue = `${value1}-${e.target.value}`;
              onChange(newValue);
            }}
            placeholder="To"
            size={size}
            type="number"
            fullWidth
          />
        </Box>
      </Tooltip>
    );
  }

  const tooltipTitle = value || getInputConfig.placeholder || "";

  return (
    <Tooltip title={tooltipTitle} arrow>
      <FormControl size={size} sx={{ flex: fullWidth ? 1 : undefined, ...sx }}>
        <TextField
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={getInputConfig.placeholder}
          size={size}
          type={getInputConfig.type === "date" ? "date" : getInputConfig.type === "number" ? "number" : "text"}
          fullWidth
          slotProps={getInputConfig.type === "date" ? { inputLabel: { shrink: true } } : undefined}
        />
      </FormControl>
    </Tooltip>
  );
};

export default memo(ConditionalValueInput);

